import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ts, P, F } from '../api';
import { Page, Section, Field, Select, Table, Td, Pill, Empty, Loading, ErrorNote, useLoad, useTS, when } from '../ui';

export default function TSCallbacks() {
  const session = useTS();
  const supervisor = session.can(P.SUPERVISE) && session.has(F.CALLBACK_CENTER);
  const canReassign = session.can(P.LEADS_ASSIGN) && session.has(F.CALLBACK_ADVANCED);
  const [scope, setScope] = useState('mine');
  const [status, setStatus] = useState('open');
  const list = useLoad(() => ts.callbacks(scope, status), [scope, status]);
  const staff = useLoad(() => (canReassign ? ts.staff() : Promise.resolve([])));
  const [adding, setAdding] = useState(false);

  const update = async (id, body, ok) => {
    try { await ts.updateCallback(id, body); if (ok) toast.success(ok); list.reload(); } catch (e) { toast.error(e.message); }
  };

  return (
    <Page title="Callback Center" subtitle="Missed calls, AI front desk requests and promised callbacks."
      actions={session.can(P.CALL) && <Button size="sm" onClick={() => setAdding(true)}><Plus className="w-4 h-4 mr-1" /> New callback</Button>}>
      {adding && <NewCallback staff={staff.data || []} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); list.reload(); }} />}
      <Section actions={(
        <div className="flex gap-2">
          {supervisor && <Select value={scope} onChange={setScope} options={[['mine', 'Mine'], ['all', 'Everyone'], ['unassigned', 'Unassigned'], ['overdue', 'Overdue']]} />}
          <Select value={status} onChange={setStatus} options={[['open', 'Open'], ['done', 'Done'], ['canceled', 'Canceled'], ['all', 'All']]} />
        </div>
      )}>
        {list.loading ? <Loading /> : (list.data || []).length === 0 ? <Empty>No callbacks here.</Empty> : (
          <Table head={['Due', 'Customer', 'Reason', 'Assigned to', 'Status', '']}>
            {list.data.map(c => (
              <tr key={c.id} data-testid={`ts-callback-${c.id}`}>
                <Td>{when(c.due_at)}{c.overdue && <Pill tone="red">Overdue</Pill>}</Td>
                <Td>
                  <div className="font-medium">{c.customer_name || c.customer_phone}</div>
                  <div className="text-xs text-muted-foreground">{c.customer_phone} · {c.source}</div>
                </Td>
                <Td className="max-w-xs">{c.reason}{c.notes && <div className="text-xs text-muted-foreground">{c.notes}</div>}</Td>
                <Td>
                  {canReassign
                    ? <Select value={c.assigned_member_id || ''} onChange={(m) => update(c.id, { assigned_member_id: m || null }, 'Reassigned')}
                      options={[['', 'Unassigned'], ...(staff.data || []).filter(m => m.status === 'active').map(m => [m.id, m.full_name])]} />
                    : c.assigned_member_name || '—'}
                </Td>
                <Td><Pill tone={c.status === 'done' ? 'green' : 'amber'}>{c.status.replace('_', ' ')}</Pill></Td>
                <Td className="whitespace-nowrap text-right">
                  {['open', 'in_progress'].includes(c.status) && (
                    <>
                      <Link to={`/telesales/phone?to=${encodeURIComponent(c.customer_phone)}${c.lead_id ? `&lead=${c.lead_id}` : ''}`} className="text-xs text-primary mr-2"
                        onClick={() => c.status === 'open' && update(c.id, { status: 'in_progress' })}>Call now</Link>
                      <Button size="sm" variant="ghost" onClick={() => {
                        const outcome = window.prompt('What happened? (optional)') || '';
                        update(c.id, { status: 'done', outcome }, 'Marked done');
                      }}>Done</Button>
                      <Button size="sm" variant="ghost" onClick={() => update(c.id, { due_at: new Date(Date.now() + 3600e3).toISOString() }, 'Moved by 1 hour')}>+1h</Button>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </Page>
  );
}

function NewCallback({ staff, onClose, onSaved }) {
  const session = useTS();
  const [form, setForm] = useState({ customer_phone: '', customer_name: '', reason: '', due_at: '', assigned_member_id: '' });
  const [error, setError] = useState('');
  const set = (k) => (v) => setForm({ ...form, [k]: typeof v === 'string' ? v : v.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      await ts.createCallback({ ...form, due_at: form.due_at ? new Date(form.due_at).toISOString() : undefined,
        assigned_member_id: form.assigned_member_id || undefined, reason: form.reason || 'Callback' });
      onSaved();
    } catch (err) { setError(err.message); }
  };
  return (
    <Section title="New callback">
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ErrorNote error={error} />
        <Field label="Customer phone"><Input required value={form.customer_phone} onChange={set('customer_phone')} /></Field>
        <Field label="Customer name"><Input value={form.customer_name} onChange={set('customer_name')} /></Field>
        <Field label="Reason"><Input value={form.reason} onChange={set('reason')} /></Field>
        <Field label="Due"><Input type="datetime-local" value={form.due_at} onChange={set('due_at')} /></Field>
        {session.can(P.LEADS_ASSIGN) && session.has(F.CALLBACK_ADVANCED) && (
          <Field label="Assign to"><Select value={form.assigned_member_id} onChange={set('assigned_member_id')} options={[['', 'Me'], ...staff.filter(m => m.status === 'active').map(m => [m.id, m.full_name])]} /></Field>
        )}
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </Section>
  );
}
